import dayjs from 'date'
import { useMutation, useQuery } from 'hooks'
import { useEffect, useMemo, useState } from 'react'

import clear from './images/clear.png'
import clearNight from './images/clearNight.png'
import storm from './images/storm.png'
import snow from './images/snow.png'
import rain from './images/rain.png'
import rainNight from './images/rainNight.png'
import fewclouds from './images/fewclouds.png'
import fewcloudsNight from './images/fewcloudsNight.png'
import clouds from './images/clouds.png'


type WeatherApiCondition = 'clear' | 'storm' | 'snow' | 'rain' | 'fewclouds' | 'clouds'

type WeatherApiData = {
  weather: {
    dt: number
    cond: WeatherApiCondition
    pressure: string
    wind: string
    speed: number
    feels_like: number
    humidity: [{ value: number }]
    temp_max: number
    forecast: {
      dt: number
      temp: {
        day: number
        night: number
      }
      cond: WeatherApiCondition
    }[]
    source: string[]
    schoolAlert: {
      temp: number
      wind: number //это условия сработки
      schoolClass: number //это класс школы (для надписи вида "отмена у 1..4 классов")
    }
  }
}

export type WeatherModifiedData = {
  date: string
  dt: number
  temp: number
  icon: string
  condition: string
  feelsLike: string
  pressure: string
  humidity: string
  wind: string
  // waterTemp: string
  // geomagnetic: string
  // pollution: string
  // waterLevel: string
  // radiation: string
  // illumination: string
  windDirection: string
  speed: string
  forecast: {
    date: string
    tempDay: number
    iconDay: string
    tempNight: number
    iconNight: string
  }[]
  source: string[]
  schoolAlert: WeatherApiData['weather']['schoolAlert']
}

const textIconMapping = [
  {
    apiConditionText: [ 'clear' ],
    conditionText: 'Ясно',
    icons: {
      day: clear,
      night: clearNight,
    },
  },
  {
    apiConditionText: [ 'storm' ],
    conditionText: 'Гроза',
    icons: {
      day: storm,
      night: storm,
    },
  },
  {
    apiConditionText: [ 'snow' ],
    conditionText: 'Снег',
    icons: {
      day: snow,
      night: snow,
    },
  },
  {
    apiConditionText: [ 'rain' ],
    conditionText: 'Дождь',
    icons: {
      day: rain,
      night: rainNight,
    },
  },
  {
    apiConditionText: [ 'fewclouds' ],
    conditionText: 'Переменная облачность',
    icons: {
      day: fewclouds,
      night: fewcloudsNight,
    },
  },
  {
    apiConditionText: [ 'clouds' ],
    conditionText: 'Облачно',
    icons: {
      day: clouds,
      night: clouds,
    },
  },
]

const useWeather = ({ datetime, subcityId }: { datetime?: string, subcityId?: string }) => {
  // datetime:"08.02.2023 15:30" //или ничего для текущей
  // subcityId: ничего или Int //столица или id из getWeatherSubcitie
  const body = useMemo(() => ({
    datetime,
    subcityId,
  }), [ datetime, subcityId ])

  const [ data, setData ] = useState<WeatherModifiedData>()
  const [ isFetching, setIsFetching ] = useState<boolean>(false)

  const [ submit ] = useMutation<WeatherApiData>({
    url: '',
  })

  useEffect(() => {
    setIsFetching(true)
    submit({
      url: `/ajax.php?action=loadWeather&v=2${!datetime ? '&forecast=1' : ''}`,
      body,
    })
      .then(({ data }: { data: WeatherApiData }) => {
        const { weather: { temp_max: temp, humidity, dt, pressure, wind, speed, feels_like: feelsLike,
          forecast: forecastRaw, source, schoolAlert, cond } } = data || {}
        const windAngle = parseInt(wind)
        const windDirection = windAngle < 90 ? 'СВ'
          : windAngle < 180 ? 'ЮВ'
            : windAngle < 270 ? 'ЮЗ'
              : 'СЗ'

        const conditionMappipngItem = textIconMapping.find((item) => item.apiConditionText.includes(cond))

        const forecast = forecastRaw?.map(({ temp: { day: tempDay, night: tempNight }, cond }, index) => ({
          date: dayjs().add(index + 1, 'days').format('DD.MM, dddd'),
          tempDay: Math.floor(tempDay),
          tempNight: Math.floor(tempNight),
          iconDay: conditionMappipngItem?.icons.day || clear,
          iconNight: conditionMappipngItem?.icons.night || clearNight,
        })) || []

        setData({
          dt,
          date: dayjs().format('DD.MM, dddd, HH:mm'),
          temp,
          condition: conditionMappipngItem?.conditionText,
          icon: conditionMappipngItem?.icons?.day,
          feelsLike: feelsLike?.toFixed(),
          pressure,
          humidity: humidity?.[0]?.value + '%',
          wind: `${Math.floor(speed)} м/с, ${windDirection}`,
          speed: `${Math.floor(speed)} м/с`,
          windDirection,
          forecast,
          source,
          schoolAlert,
        })
        setIsFetching(false)
      })
  }, [ body, datetime, submit ])

  return { ...data, isWeatherFetching: isFetching }
}

export default useWeather